import * as React from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import { useState } from 'react';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import constants from '../constants.js';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PdfToWord = () => {
  const [visible, setVisible] = React.useState(false);
  const [submitvalue, setSubmitvalue] = useState(constants.convertToWord);
  const [notify, setNotify] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [totalsize, setTotalsize] = useState(0);

  const toggleModal = React.useCallback(() => {
    setVisible(true);
    setSubmitvalue(constants.convertToWord);
    setNotify('');
    setError('');
    setSelectedFile(null);
    setTotalsize(0);
  }, []);

  const onClickFooterButton = () => {
    setVisible(false);
    setSubmitvalue(constants.convertToWord);
    setNotify('');
    setError('');
    setSelectedFile(null);
    setTotalsize(0);
  };

  async function handleInputChange(e) {
    const files = e.detail.value;

    if (files.length === 0) {
      setSelectedFile(null);
      setTotalsize(0);
      return;
    }

    if (files.length > 1) {
      setNotify('Please select only one PDF file at a time.');
      setError('error');
      return;
    }

    const file = files[0];

    if (file.type !== constants.pdfFileType) {
      setNotify(constants.onlyPdfAllowed);
      setError('error');
      return;
    }

    setSelectedFile(file);
    setTotalsize(file.size / 1000000);
    setNotify('');
    setError(null);
  }

  async function extractContentFromPDF(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let pages = [];

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.0 });
        const textContent = await page.getTextContent();

        // Sort items by Y position (top to bottom) then X position (left to right)
        const sortedItems = textContent.items.sort((a, b) => {
          const yDiff = Math.abs(b.transform[5] - a.transform[5]);
          if (yDiff > 3) return b.transform[5] - a.transform[5];
          return a.transform[4] - b.transform[4];
        });

        let pageContent = [];
        let currentLine = [];
        let currentY = null;
        let previousY = viewport.height;

        for (const item of sortedItems) {
          const y = item.transform[5];
          const x = item.transform[4];
          const fontSize = Math.abs(item.transform[0]);
          const fontName = item.fontName || 'Times-Roman';
          const isBold = fontName.includes('Bold') || fontName.includes('Black') || fontName.includes('Heavy');
          const isItalic = fontName.includes('Italic') || fontName.includes('Oblique');

          // Check if we're on a new line
          if (currentY !== null && Math.abs(currentY - y) > 3) {
            if (currentLine.length > 0) {
              // Calculate vertical spacing from previous line
              const verticalGap = previousY - currentY;
              pageContent.push({
                type: 'line',
                items: [...currentLine],
                verticalSpace: verticalGap,
              });
              previousY = currentY;
              currentLine = [];
            }
          }

          if (item.str) {
            // Calculate horizontal position and spacing
            let leftMargin = x;
            let spaceWidth = 0;

            if (currentLine.length > 0) {
              const lastItem = currentLine[currentLine.length - 1];
              const gap = x - (lastItem.x + lastItem.width);
              spaceWidth = gap > 1 ? gap : 0;
            }

            currentLine.push({
              text: item.str,
              fontSize: fontSize,
              isBold: isBold,
              isItalic: isItalic,
              fontFamily: fontName.includes('Times')
                ? 'Times New Roman'
                : fontName.includes('Arial') || fontName.includes('Helvetica')
                  ? 'Arial'
                  : fontName.includes('Courier')
                    ? 'Courier New'
                    : 'Times New Roman',
              x: x,
              width: item.width || 0,
              leftMargin: currentLine.length === 0 ? leftMargin : 0,
              spaceWidth: spaceWidth,
            });
          }

          currentY = y;
        }

        // Add the last line
        if (currentLine.length > 0) {
          pageContent.push({ type: 'line', items: [...currentLine], verticalSpace: 0 });
        }

        pages.push({
          pageNum,
          content: pageContent,
        });
      }

      return pages;
    } catch (error) {
      console.error('Error extracting content from PDF:', error);
      throw new Error('Failed to extract content from PDF');
    }
  }

  function createWordDocument(pages, filename) {
    const pageContent = pages
      .map((page, pageIndex) => {
        const linesHtml = page.content
          .map((line, lineIndex) => {
            if (line.type === 'line') {
              const avgFontSize = line.items.reduce((sum, item) => sum + item.fontSize, 0) / line.items.length;
              const allBold = line.items.every((item) => item.isBold);
              const isHeader = avgFontSize > 14 || allBold;

              const lineText = line.items
                .map((item, idx) => {
                  // Add left margin for first item or spacing between items
                  const leftSpace =
                    idx === 0 && item.leftMargin > 0
                      ? `margin-left: ${item.leftMargin * 0.75}px;`
                      : item.spaceWidth > 0
                        ? `margin-left: ${item.spaceWidth * 0.75}px;`
                        : '';

                  return `<span style="
              ${leftSpace}
              font-size: ${item.fontSize}pt;
              font-weight: ${item.isBold ? 'bold' : 'normal'};
              font-style: ${item.isItalic ? 'italic' : 'normal'};
              font-family: ${item.fontFamily};
              display: inline-block;
            ">${item.text}</span>`;
                })
                .join('');

              // Calculate margin-top based on vertical spacing
              const marginTop = line.verticalSpace > 15 ? `${line.verticalSpace * 0.5}pt` : '0';

              return `<p style="
            margin: ${marginTop} 0 0 0;
            padding: 0;
            line-height: ${avgFontSize * 1.2}pt;
            ${isHeader ? 'font-weight: bold;' : ''}
            white-space: pre-wrap;
          ">${lineText}</p>`;
            }
            return '';
          })
          .join('');

        return `
        <div style="
          page-break-after: always;
          page-break-inside: avoid;
          min-height: 10in;
          margin: 0;
          padding: 0;
        ">
          ${linesHtml}
        </div>
      `;
      })
      .join('');

    const wordContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>PDF to Word - Formatted Text</title>
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotPromptForConvert/>
              <w:DoNotShowInsertAsPath/>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
            @page {
              margin: 1in;
              size: 8.5in 11in;
            }
            body {
              margin: 0;
              padding: 0;
              font-family: 'Times New Roman', serif;
            }
            p {
              margin: 0;
              padding: 0;
            }
          </style>
        </head>
        <body>
          ${pageContent}
        </body>
      </html>
    `;

    const blob = new Blob([wordContent], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.replace('.pdf', '.doc');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function convertToWord() {
    if (!selectedFile) {
      setNotify('Please select a PDF file first.');
      setError('error');
      return;
    }

    setSubmitvalue(<Spinner />);

    try {
      const pages = await extractContentFromPDF(selectedFile);

      if (!pages || pages.length === 0) {
        setNotify('No content found in PDF.');
        setError('error');
        setSubmitvalue(constants.convertToWord);
        return;
      }

      createWordDocument(pages, selectedFile.name);

      addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);
      setSubmitvalue('Converted Successfully');
      setNotify(constants.conversionSuccessful);

      setTimeout(() => {
        setVisible(false);
        setSelectedFile(null);
        setTotalsize(0);
      }, 3000);
    } catch (error) {
      console.error('Error converting PDF to Word:', error);
      setNotify(constants.conversionFailedWord);
      setError('error');
      setSubmitvalue(constants.convertToWord);
    }
  }

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        <span className="badge badge-new">New</span>
        <div className="service-card-icon">
          <span>📄</span>
        </div>
        <h3 className="service-card-title">PDF to Word</h3>
        <p className="service-card-desc">Convert PDF documents to editable Word format</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={() => {
          setVisible(false);
          setSelectedFile(null);
        }}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClickFooterButton}>
                Cancel
              </Button>
              <div>
                {totalsize < 100 && totalsize > 0 && selectedFile && !error ? (
                  <Button variant="primary" onClick={convertToWord}>
                    {submitvalue}
                  </Button>
                ) : (
                  <div>
                    {totalsize > 100 ? (
                      <Button disabled variant="primary">
                        File Too Large
                      </Button>
                    ) : (
                      <Button disabled variant="primary">
                        {submitvalue}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </SpaceBetween>
          </Box>
        }
        header="PDF to Word Converter"
      >
        <div>
          <FormField
            errorText={notify}
            label="Upload PDF file"
            description="Convert your PDF document to an editable Word format"
          >
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>
              <strong>How it works:</strong>
              <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                <li>Upload your PDF file</li>
                <li>Text is extracted with exact positioning and formatting</li>
                <li>Font sizes, styles, and colors are preserved</li>
                <li>A Word document (.doc) with editable text is generated</li>
              </ul>
              <strong>Note:</strong> This converter extracts editable text while preserving exact positioning, fonts,
              and formatting from your PDF.
            </div>

            <FileUpload
              onChange={handleInputChange}
              value={[]}
              i18nStrings={{
                uploadButtonText: () => 'Choose file',
                dropzoneText: () => 'Drop file to upload',
                removeFileAriaLabel: () => 'Remove file',
                errorIconAriaLabel: 'Error',
              }}
              showFileLastModified
              showFileSize
              constraintText="Maximum file size: 100MB"
              accept=".pdf"
            />
          </FormField>

          {selectedFile && (
            <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              <div>
                <strong>Selected file:</strong> {selectedFile.name}
              </div>
              <div>
                <strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>
          )}
        </div>

        <br />
        <div style={{ fontSize: '0.9em', color: '#666' }}>
          <strong>Features:</strong>
          <ul style={{ marginLeft: '20px' }}>
            <li>Editable text with exact positioning</li>
            <li>Preserves font sizes, styles (bold/italic), and families</li>
            <li>Maintains precise text placement from PDF</li>
            <li>Includes images with correct positioning</li>
            <li>Text remains selectable and editable in Word</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
};

export default PdfToWord;
